import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { CatalogItemSchema, type CatalogItem, type ItemKind } from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

const CatalogResponseSchema = z.array(CatalogItemSchema);

export type CatalogKind = ItemKind; // 'insumo' | 'medicamento'

export const catalogKeys = {
  list: (kind: CatalogKind, q: string, limit: number, includeInactive: boolean) =>
    ['catalog', kind, 'list', q, limit, includeInactive] as const,
  // Claves heredadas (sin includeInactive) — para que el buscador de la hoja
  // de gasto siga compartiendo cache cuando no pasa `includeInactive`.
  supplies: (q: string, limit: number) => ['catalog', 'supplies', q, limit] as const,
  medications: (q: string, limit: number) => ['catalog', 'medications', q, limit] as const,
};

function endpointFor(kind: CatalogKind): string {
  return kind === 'insumo' ? '/api/catalog/supplies' : '/api/catalog/medications';
}

function buildQs(params: {
  q?: string;
  limit: number;
  includeInactive?: boolean;
}): string {
  const usp = new URLSearchParams();
  if (params.q !== undefined && params.q.trim() !== '') usp.set('q', params.q.trim());
  usp.set('limit', String(params.limit));
  if (params.includeInactive === true) usp.set('includeInactive', 'true');
  return usp.toString();
}

/**
 * Hook genérico — los componentes de búsqueda del flujo de gasto usan los
 * shortcuts `useSearchSupplies`/`useSearchMedications` (activos solamente).
 * La página de administración del catálogo usa este directamente con
 * `includeInactive: true`.
 */
export function useCatalogList(
  kind: CatalogKind,
  options: { q?: string; limit?: number; includeInactive?: boolean; enabled?: boolean } = {},
): UseQueryResult<CatalogItem[], Error> {
  const q = options.q ?? '';
  const limit = options.limit ?? 50;
  const includeInactive = options.includeInactive ?? false;
  return useQuery({
    queryKey: catalogKeys.list(kind, q, limit, includeInactive),
    queryFn: () =>
      apiFetch<CatalogItem[]>(`${endpointFor(kind)}?${buildQs({ q, limit, includeInactive })}`, {
        parseAs: CatalogResponseSchema,
      }),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/* ----- Atajos para el buscador del flujo de gasto (activos solamente). ----- */

export function useSearchSupplies(
  q: string,
  options: { limit?: number; enabled?: boolean } = {},
): UseQueryResult<CatalogItem[], Error> {
  const limit = options.limit ?? 20;
  return useQuery({
    queryKey: catalogKeys.supplies(q, limit),
    queryFn: () =>
      apiFetch<CatalogItem[]>(
        `/api/catalog/supplies?${buildQs({ q, limit, includeInactive: false })}`,
        { parseAs: CatalogResponseSchema },
      ),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useSearchMedications(
  q: string,
  options: { limit?: number; enabled?: boolean } = {},
): UseQueryResult<CatalogItem[], Error> {
  const limit = options.limit ?? 20;
  return useQuery({
    queryKey: catalogKeys.medications(q, limit),
    queryFn: () =>
      apiFetch<CatalogItem[]>(
        `/api/catalog/medications?${buildQs({ q, limit, includeInactive: false })}`,
        { parseAs: CatalogResponseSchema },
      ),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/* -------------------------- Mutations (admin) -------------------------- */

export interface CreateCatalogItemInput {
  codigo: string;
  nombre: string;
}

export interface UpdateCatalogItemInput {
  nombre?: string;
  activo?: boolean;
}

export function useCreateCatalogItem(
  kind: CatalogKind,
): UseMutationResult<CatalogItem, Error, CreateCatalogItemInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch<CatalogItem>(endpointFor(kind), {
        method: 'POST',
        body: input,
        parseAs: CatalogItemSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog', kind] });
      // También invalidamos las claves heredadas del buscador.
      void qc.invalidateQueries({
        queryKey: kind === 'insumo' ? ['catalog', 'supplies'] : ['catalog', 'medications'],
      });
    },
  });
}

export function useUpdateCatalogItem(
  kind: CatalogKind,
): UseMutationResult<CatalogItem, Error, { codigo: string; body: UpdateCatalogItemInput }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ codigo, body }) =>
      apiFetch<CatalogItem>(`${endpointFor(kind)}/${encodeURIComponent(codigo)}`, {
        method: 'PATCH',
        body,
        parseAs: CatalogItemSchema,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['catalog', kind] });
      void qc.invalidateQueries({
        queryKey: kind === 'insumo' ? ['catalog', 'supplies'] : ['catalog', 'medications'],
      });
    },
  });
}

export type { CatalogItem };
